import React from 'react';
import FluidButton from 'ui/FluidButton';
import { ItineraryImage } from './items/ItineraryImage';
import { ItineraryTypes } from './types';
import { useSelector } from 'react-redux';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { useHistory } from 'react-router-dom';
import { useIsInViewport } from 'hooks/useIsInViewport';
import classNames from 'classnames';
import { ButtonWithDropdown } from 'ui/ButtonWithDropdown';
import { formatDateDisplay } from 'utils';

const ItineraryLinks = (props: { days: { day: number; isFirst: boolean }[] }) => {
  const dayLinks = props.days.filter(d => d.isFirst);

  const isContactInView = useIsInViewport('itinerary-contact');
  const isItineraryInView = useIsInViewport('main-itinerary-wrapper') && !isContactInView;

  return (
    <div className="actions flex items-end justify-end gap-2 mx-auto">
      <ButtonWithDropdown
        buttonLabel="Itinerary"
        buttonClassName={classNames('h-full border-y-4 border-y-white text-black', {
          'border-b-teal-100 hover:border-t-gray-10': isItineraryInView,
          'hover:border-y-gray-10': !isItineraryInView,
        })}
        closeOnSelect={true}
        dropdownOptions={dayLinks.map(dl => {
          return {
            label: (
              <span className="flex items-center gap-2">
                <span>DAY {dl.day}</span>
              </span>
            ),
            onClick: () => {
              const id = `itinerary-day-${dl.day}`;
              const matchingDayElement = document.getElementById(id);
              if (matchingDayElement) {
                matchingDayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            },
          };
        })}
      />

      <button
        onClick={() => {
          const id = 'itinerary-contact';
          const matchingDayElement = document.getElementById(id);
          if (matchingDayElement) {
            matchingDayElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
        className={classNames(
          'm-0 p-0 px-3 h-full py-2 bg-transparent font-hurmegeometric-sans text-15px hover:bg-gray-10 hover:border-y-gray-10 rounded-3px uppercase cursor-pointer box-border border-y-4 border-y-white text-black',
          {
            'border-b-teal-100 hover:border-t-gray-10': isContactInView,
            'hover:border-y-gray-10': !isContactInView,
          }
        )}
      >
        Contact
      </button>
    </div>
  );
};

interface iItineraryHeaderProps {
  upload: ItineraryTypes['ItineraryImageUploadOrThumbnail'] | undefined;
  changeLogoButton?: React.ReactNode;
  mode: 'edit' | 'view';
  itineraryUuid: string;
  version: string;
  isLatestVersion: boolean;
  onSave?: () => void;
  onPublish?: () => void;
  days?: { day: number; isFirst: boolean }[];
  isSaveLoading?: boolean;
  isPublishLoading?: boolean;
  canViewItineraryLinks: boolean;
  publishedTimestamp?: string;
  bookingRef?: string;
}

export const ItineraryHeader = (props: iItineraryHeaderProps) => {
  const isInternalUser = useSelector(AuthSelectors.isInternalUser);
  const history = useHistory();
  return (
    <div
      id="itinerary-header"
      className="sticky w-full my-0 top-0 left-0 right-0 flex flex-col bg-white z-[9999] shadow-sm"
    >
      <div className="flex flex-col md:flex-row items-center justify-between px-[32px]">
        {props.upload && (
          <div className="flex items-center gap-2 h-full py-10px md:max-w-[50%]">
            <ItineraryImage index={0} className="h-full" imgClassName="max-h-[40px]" upload={props.upload} />
            {props.changeLogoButton ? props.changeLogoButton : null}
          </div>
        )}

        {props.mode === 'edit' && (
          <div className="flex items-center gap-2 mr-[32px] py-10px">
            <FluidButton
              type="secondary"
              onClick={() => {
                history.push(`/itinerary/${props.itineraryUuid}/${props.version}`);
              }}
            >
              Discard
            </FluidButton>
            <FluidButton
              isLoading={props.isSaveLoading || false}
              type="primary"
              onClick={() => {
                props.onSave && props.onSave();
              }}
            >
              Save
            </FluidButton>
            <FluidButton
              isLoading={props.isPublishLoading || false}
              type="primary"
              onClick={() => {
                props.onPublish && props.onPublish();
              }}
            >
              Publish
            </FluidButton>
          </div>
        )}

        {isInternalUser && props.mode === 'view' && props.isLatestVersion && (
          <div className="flex items-center gap-2 mr-[32px] py-10px">
            <FluidButton
              type="secondary"
              onClick={() => {
                history.push(`/itinerary/${props.itineraryUuid}/${props.version}/edit`);
              }}
            >
              Edit
            </FluidButton>
            <FluidButton
              isLoading={props.isPublishLoading || false}
              type="primary"
              onClick={() => {
                props.onPublish && props.onPublish();
              }}
            >
              Publish
            </FluidButton>
          </div>
        )}

        {/* this is effectively published version */}
        {props.mode === 'view' && props.bookingRef && props.publishedTimestamp && (
          <div className="flex flex-row md:flex-col w-full pb-10px gap-1 text-12px md:text-14px justify-between">
            <p className="m-0 p-0 text-flint md:text-right">
              <span className="font-bold">Booking Ref:</span>{' '}
              <span className="block md:inline-block">{props.bookingRef}</span>
            </p>
            <p className="m-0 p-0 text-flint md:text-right">
              <span className="font-bold">Version:</span>{' '}
              <span className="block md:inline-block">
                {props.version} ({formatDateDisplay(props.publishedTimestamp)})
              </span>
            </p>
          </div>
        )}
      </div>

      {props.canViewItineraryLinks === true && props.mode === 'view' && props.days && (
        <div className="flex justify-between border-t-1 border-gray-10 w-full">
          <ItineraryLinks days={props.days} />
        </div>
      )}
    </div>
  );
};
